import React from 'react';
import { Mafs, Coordinates, Plot, Point, Vector, Line, Theme, useMovablePoint, Transform, Text } from 'mafs';

export const UnitCircle = ({ config = {}, variables = {} }) => {
    // 1. Config & State
    const initialAngle = Number.isFinite(variables.angleDegrees)
        ? variables.angleDegrees
        : (Number.isFinite(config.angleDegrees) ? config.angleDegrees : 45);

    const showSin = config.showSin !== false;
    const showCos = config.showCos !== false;
    const showTan = config.showTan === true;

    // Interactive point on Unit Circle
    const point = useMovablePoint([
        Math.cos((initialAngle * Math.PI) / 180),
        Math.sin((initialAngle * Math.PI) / 180)
    ], {
        constrain: ([x, y]) => {
            const angle = Math.atan2(y, x);
            return [Math.cos(angle), Math.sin(angle)];
        }
    });

    const angleRad = Math.atan2(point.y, point.x);
    // Normalize 0 to 2PI for the wave graph
    const plotAngle = angleRad < 0 ? angleRad + 2 * Math.PI : angleRad;
    const angleDeg = (plotAngle * 180) / Math.PI;

    // Tangent logic
    const tanY = Math.tan(angleRad);
    const isTanVisible = Math.abs(angleRad) < Math.PI / 2.1; // Hide when near vertical

    // Wave Offset (Separation between circle and wave)
    const WAVE_OFFSET = 2.5;

    return (
        <div className="flexColumn gap-medium width-100">
            <div className="ch-emph-block" style={{ height: 350, overflow: 'hidden' }}>
                <Mafs viewBox={{ x: [-1.5, 8], y: [-2, 2] }} pan={false}>
                    <Coordinates.Cartesian
                        xAxis={{ lines: false, labels: false }}
                        yAxis={{ lines: false, labels: false }}
                    />

                    {/* === LEFT: UNIT CIRCLE === */}
                    <g>
                        <Plot.Parametric
                            t={[0, 2 * Math.PI]}
                            xy={t => [Math.cos(t), Math.sin(t)]}
                            color={Theme.gray} opacity={0.5}
                        />
                        <Line.Segment point1={[-1.2, 0]} point2={[1.2, 0]} color="#ccc" />
                        <Line.Segment point1={[0, -1.2]} point2={[0, 1.2]} color="#ccc" />

                        {/* Angle Wedge */}
                        <Plot.Parametric
                            t={[0, plotAngle]}
                            xy={t => [0.2 * Math.cos(t), 0.2 * Math.sin(t)]}
                            color={Theme.indigo}
                        />

                        {/* Radius Vector */}
                        <Vector tail={[0, 0]} tip={[point.x, point.y]} color="#1e293b" weight={2} />

                        {/* Components */}
                        {showSin && <Line.Segment point1={[point.x, 0]} point2={[point.x, point.y]} color={Theme.blue} weight={3} />}
                        {showCos && <Line.Segment point1={[0, 0]} point2={[point.x, 0]} color={Theme.green} weight={3} />}

                        {/* Tangent (only visible right side) */}
                        {showTan && isTanVisible && (
                            <>
                                <Line.Segment point1={[1, 0]} point2={[1, tanY]} color={Theme.orange} weight={2} />
                                <Line.Segment point1={[0, 0]} point2={[1, tanY]} style="dashed" color="#999" opacity={0.5} />
                            </>
                        )}

                        <Point x={point.x} y={point.y} color={Theme.red} />
                    </g>

                    {/* === RIGHT: SINE WAVE === */}
                    <g transform={`translate(${WAVE_OFFSET}, 0)`}>
                        {/* We can use Transform wrapper or manual offset.
                            Using Transform wrapper for cleaner Plot code */}
                        <Transform translate={[WAVE_OFFSET, 0]}>
                            <Line.Segment point1={[0, 0]} point2={[2 * Math.PI, 0]} color="#ccc" />

                            {/* Full Wave Ghost */}
                            <Plot.OfX y={x => Math.sin(x)} t={[0, 2 * Math.PI]} color={Theme.blue} opacity={0.3} />

                            {/* Traced Path */}
                            <Plot.OfX y={x => Math.sin(x)} t={[0, plotAngle]} color={Theme.blue} weight={3} />

                            <Point x={plotAngle} y={Math.sin(plotAngle)} color={Theme.blue} />

                            <Text x={0} y={-1.5} size={14} color="#666">0</Text>
                            <Text x={Math.PI} y={-1.5} size={14} color="#666">π</Text>
                            <Text x={2*Math.PI} y={-1.5} size={14} color="#666">2π</Text>
                        </Transform>
                    </g>

                    {/* === CONNECTOR LINE (Global Coords) === */}
                    {/* Connects Circle point (x,y) to Wave point (x + offset, y) */}
                    <Line.Segment
                        point1={[point.x, point.y]}
                        point2={[WAVE_OFFSET + plotAngle, Math.sin(plotAngle)]}
                        style="dashed"
                        color={Theme.blue}
                        opacity={0.4}
                    />

                    <Text x={0} y={1.6} size={20} weight={600} color="#333">Unit Circle</Text>
                    <Text x={5.5} y={1.6} size={20} weight={600} color="#333">y = sin(θ)</Text>
                </Mafs>
            </div>

            {/* Readout */}
            <div className="ch-card padding--m flexRow gap-large flexAlignCentre justify-center">
                <div className="text-center">
                    <div className="ch-text-small text-muted font-bold uppercase">Angle (θ)</div>
                    <div className="ch-text-xl font-mono">{angleDeg.toFixed(0)}°</div>
                </div>
                {showSin && (
                    <div className="text-center" style={{ color: Theme.blue }}>
                        <div className="ch-text-small font-bold uppercase">Sin(θ)</div>
                        <div className="ch-text-xl font-mono">{Math.sin(plotAngle).toFixed(3)}</div>
                    </div>
                )}
                {showCos && (
                    <div className="text-center" style={{ color: Theme.green }}>
                        <div className="ch-text-small font-bold uppercase">Cos(θ)</div>
                        <div className="ch-text-xl font-mono">{Math.cos(plotAngle).toFixed(3)}</div>
                    </div>
                )}
                {showTan && (
                    <div className="text-center" style={{ color: Theme.orange }}>
                        <div className="ch-text-small font-bold uppercase">Tan(θ)</div>
                        <div className="ch-text-xl font-mono">
                            {!isTanVisible ? 'Undefined' : Math.tan(plotAngle).toFixed(3)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};